import { FocusSession } from '../types';

// Common utility functions

// Format time in MM:SS format
export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Format duration in human readable format
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

// Format duration from hours string (e.g., "6.3") to human readable format (e.g., "6h 18m")
export const formatHours = (hours: number): string => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);

  if (h === 0) {
    return `${m}m`;
  }

  if (m === 0) {
    return `${h}h`;
  }

  return `${h}h ${m}m`;
};

// Format date
export const formatDate = (date: Date | string, options: Intl.DateTimeFormatOptions = {}): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };

  return new Date(date).toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

// Format date and time
export const formatDateTime = (date: Date | string): string => {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Debounce function
export const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): T => {
  let timeout: NodeJS.Timeout | undefined;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  } as T;
};

// Throttle function
export const throttle = <T extends (...args: any[]) => any>(func: T, limit: number): T => {
  let inThrottle: boolean | undefined;
  return function (this: any, ...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  } as T;
};

// Generate random ID
export const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

// Local storage helpers
export const storage = {
  get: <T = any>(key: string, defaultValue: T | null = null): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },

  set: <T = any>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  }
};

// Invoice profile (personal + bank details) - used for pre-filling invoice form
const INVOICE_PROFILE_KEY = 'focuzon_invoice_profile';

export interface InvoiceProfile {
  senderName?: string;
  senderAddress?: string;
  senderEmail?: string;
  payeeName?: string;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  pan?: string;
}

export const invoiceProfile = {
  get: (): InvoiceProfile => storage.get<InvoiceProfile>(INVOICE_PROFILE_KEY, {}) || {},
  set: (data: InvoiceProfile): void => {
    storage.set(INVOICE_PROFILE_KEY, { ...invoiceProfile.get(), ...data });
  },
};

// Validate email
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Calculate focus score
export const calculateFocusScore = (sessions: FocusSession[]): number => {
  if (!sessions || sessions.length === 0) return 0;

  const totalMinutes = sessions.reduce((sum, session) => sum + session.duration, 0);
  const completedSessions = sessions.filter(session => session.completed).length;
  const completionRate = completedSessions / sessions.length;

  // Base score from completion rate (0-50 points)
  const completionScore = completionRate * 50;

  // Bonus for consistency (0-30 points)
  const consistencyScore = Math.min(30, totalMinutes / 10);

  // Bonus for streak (0-20 points)
  const streakBonus = Math.min(20, sessions.length * 2);

  return Math.round(completionScore + consistencyScore + streakBonus);
};
